<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <VariableSelect v-if="!isAgent" label="Assign Response to Variable" v-model="config.variable" class="w-full" system :filter-data-type="['DataTable']" @update:modelValue="emitPayload" />

    <FormSelect
      :label="$t('evaluate.data_adapter')"
      :clearable="true"
      v-model="config.adapter_type"
      :options="dataAdapters"
      default-reducer
      class="mt-3 w-full"
      @update:modelValue="onDataAdapterChange"
    />

    <template v-if="config.adapter_type">
      <template v-if="isAgent">
        <FormSelect
          label="Tools"
          :clearable="true"
          multiple
          v-model="selectedToolNames"
          :options="toolsList"
          default-reducer
          class="mt-3 w-full"
          :loading="toolsFetching"
          @update:modelValue="onAgentToolsChange"
        />
        <div v-if="tools.length" class="flex justify-end gap-x-2 mt-2">
          <PillButton text="Select all" small outlined @click="selectAllTools" />
          <PillButton text="Clear" small outlined @click="clearTools" />
        </div>
      </template>

      <template v-else>
        <FormSelect
          label="Tool"
          :clearable="true"
          v-model="config.tool_name"
          :options="toolsList"
          default-reducer
          class="mt-3 w-full"
          :loading="toolsFetching"
          @update:modelValue="onWorkflowToolChange"
        />

        <div v-if="selectedTool" class="mt-4">
          <div class="flex items-center text-12 font-600 border-b border-solid border-gray-200 pb-1">Tool Parameters</div>
          <div v-if="!toolParameters.length" class="text-12 text-semi-dark-text mt-3">This tool does not require parameters.</div>
          <div v-for="parameter in toolParameters" :key="parameter.name" class="border border-solid border-stroke rounded-8 p-3 mt-3">
            <div class="flex items-start justify-between gap-x-3">
              <div class="min-w-0">
                <div class="font-600 text-13 truncate">{{ parameter.name }}</div>
                <div class="text-12 text-semi-dark-text mt-1">{{ parameter.description || parameter.type }}</div>
              </div>
              <Badge v-if="parameter.required" text="Required" warn small />
            </div>

            <div class="flex mt-3 gap-x-3 flex-wrap">
              <FormSelect
                v-model="config.arguments[parameter.name].assignment_type"
                label="Assign"
                :options="assignmentTypes"
                default-reducer
                :clearable="false"
                class="w-150 flex-shrink-0"
                @update:modelValue="onArgumentTypeChange(parameter)"
              />

              <FormSelect
                v-model="config.arguments[parameter.name].dataType"
                label="Data Type"
                :options="getParameterDataTypes(parameter)"
                default-reducer
                :clearable="false"
                class="w-180 flex-shrink-0"
                @update:modelValue="onArgumentValueChange(parameter)"
              />

              <div v-if="config.arguments[parameter.name].assignment_type === 'variable'" class="flex-grow min-w-200">
                <VariableSelect
                  label="Variable"
                  v-model="config.arguments[parameter.name].value"
                  :filter="(variable) => variableFilter(variable, parameter)"
                  @update:modelValue="onArgumentValueChange(parameter)"
                />
              </div>

              <div v-else class="flex-grow min-w-200">
                <FormSelect
                  v-if="config.arguments[parameter.name].dataType === 'YesNo'"
                  v-model="config.arguments[parameter.name].value"
                  label="Value"
                  :options="['Yes', 'No']"
                  append-to-body
                  @update:modelValue="onArgumentValueChange(parameter)"
                />
                <FormInput
                  v-else-if="config.arguments[parameter.name].dataType === 'Number'"
                  v-model="config.arguments[parameter.name].value"
                  type="number"
                  label="Value"
                  @update:modelValue="onArgumentValueChange(parameter)"
                />
                <PhraseEditor v-else v-model="config.arguments[parameter.name].value" :items="variables" label="Value" class="w-full" @update:modelValue="onArgumentValueChange(parameter)" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { DATA_ADAPTERS } from '@/constants';
import debounce from '../../../helpers/debounce';
import uuidv4 from '../../../helpers/uuid';
import EvaluateItemsConfig from './EvaluateItemsConfig';

const TYPE_MAP = {
  string: 'Text',
  text: 'Text',
  varchar: 'Text',
  integer: 'Number',
  number: 'Number',
  float: 'Number',
  decimal: 'Number',
  numeric: 'Number',
  boolean: 'YesNo',
  // API adapters (e.g. LUSHA) take/return DataTables; an array/object param binds to a DataTable variable.
  array: 'DataTable',
  object: 'DataTable',
};

export default {
  name: 'DataAdapterConfig',
  props: {
    item: {
      type: Object,
      default: null,
    },
    isAgent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      config: structuredClone(EvaluateItemsConfig['data-adapter'].payload),
      tools: [],
      toolsFetching: false,
      selectedToolNames: [],
    };
  },
  computed: {
    ...mapState(['availableDataAdapters', 'projectData']),
    ...mapGetters(['getProjectDataAdapters', 'getProjectDataTypes']),
    projectId() {
      return this.$route.params.projectId;
    },
    payload() {
      return {
        id: this.item.id,
        type: this.item.type,
        payload: this.normalizedPayload(),
      };
    },
    dataAdapters() {
      return Object.keys(this.getProjectDataAdapters()).map((key) => {
        const adapter = this.getProjectDataAdapters()[key];
        return {
          label: (DATA_ADAPTERS[key] && DATA_ADAPTERS[key].name) || adapter.payload?.name || this.availableDataAdapters[adapter.type]?.name || key,
          value: key,
        };
      });
    },
    toolsList() {
      return this.tools.map((tool) => ({ label: tool.description ? `${tool.name} - ${tool.description}` : tool.name, value: tool.name }));
    },
    selectedTool() {
      return this.tools.find((tool) => tool.name === this.config.tool_name) || null;
    },
    toolParameters() {
      const properties = this.selectedTool?.parameters?.properties || {};
      const required = this.selectedTool?.parameters?.required || [];
      return Object.keys(properties).map((name) => ({ name, required: required.includes(name), ...properties[name] }));
    },
    assignmentTypes() {
      return [
        { label: this.$t('variable'), value: 'variable' },
        { label: this.$t('value'), value: 'value' },
      ];
    },
    variables() {
      return Object.keys(this.projectData?.[this.projectId]?.variables || {})
        .map((variable) => ({ id: uuidv4(), value: variable }))
        .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
    },
    projectVariables() {
      return this.projectData?.[this.projectId]?.variables || {};
    },
  },
  created() {
    this.itemChangeHandler();
  },
  methods: {
    ...mapActions(['fetchProjectDataAdapterTools']),
    emitPayload: debounce(function emitDataAdapterPayload() {
      this.$emit('valid', this.validate());
      this.$emit('update', this.payload);
    }, 300),
    normalizedPayload() {
      const payload = JSON.parse(JSON.stringify(this.config));
      if (this.isAgent) {
        payload.variable = '';
        payload.tool_name = '';
        payload.arguments = {};
        payload.tools = this.tools.filter((tool) => this.selectedToolNames.includes(tool.name)).map((tool) => ({ name: tool.name, description: tool.description, parameters: tool.parameters }));
      } else {
        payload.tools = [];
      }
      return payload;
    },
    async onDataAdapterChange() {
      this.config.tool_name = '';
      this.config.tools = [];
      this.config.arguments = {};
      this.selectedToolNames = [];
      await this.fetchTools();
      this.emitPayload();
    },
    async fetchTools() {
      this.tools = [];
      if (!this.config.adapter_type) return;
      this.toolsFetching = true;
      const response = await this.fetchProjectDataAdapterTools({ projectId: this.projectId, adapterType: this.config.adapter_type });
      this.toolsFetching = false;
      this.tools = response?.tools || [];
      this.selectedToolNames = this.config.tools?.map((tool) => (typeof tool === 'string' ? tool : tool.name)) || [];
    },
    onAgentToolsChange() {
      this.config.tools = this.tools.filter((tool) => this.selectedToolNames.includes(tool.name)).map((tool) => ({ name: tool.name, description: tool.description, parameters: tool.parameters }));
      this.emitPayload();
    },
    selectAllTools() {
      this.selectedToolNames = this.tools.map((tool) => tool.name);
      this.onAgentToolsChange();
    },
    clearTools() {
      this.selectedToolNames = [];
      this.onAgentToolsChange();
    },
    onWorkflowToolChange() {
      this.config.arguments = {};
      this.toolParameters.forEach((parameter) => {
        this.ensureArgumentBinding(parameter);
      });
      this.emitPayload();
    },
    ensureArgumentBinding(parameter) {
      if (!this.config.arguments[parameter.name]) {
        this.config.arguments[parameter.name] = {
          assignment_type: 'variable',
          dataType: this.getDefaultDataType(parameter),
          value: '',
        };
      }
      if (!this.config.arguments[parameter.name].dataType) {
        this.config.arguments[parameter.name].dataType = this.getDefaultDataType(parameter);
      }
    },
    onArgumentTypeChange(parameter) {
      this.ensureArgumentBinding(parameter);
      this.config.arguments[parameter.name].value = '';
      this.onArgumentValueChange(parameter);
    },
    onArgumentValueChange(parameter) {
      this.ensureArgumentBinding(parameter);
      this.emitPayload();
    },
    getDefaultDataType(parameter) {
      return TYPE_MAP[String(parameter.type || '').toLowerCase()] || 'Text';
    },
    getParameterDataTypes(parameter) {
      const dataTypes = ['Text', 'Number', 'YesNo'];
      const defaultType = this.getDefaultDataType(parameter);
      if (!dataTypes.includes(defaultType)) dataTypes.push(defaultType);
      return dataTypes.map((dataType) => ({ label: dataType, value: dataType }));
    },
    variableFilter(variable, parameter) {
      const binding = this.config.arguments[parameter.name];
      return !binding?.dataType || variable.dataType === binding.dataType;
    },
    validate() {
      if (!this.config.adapter_type) return false;
      if (this.isAgent) return this.selectedToolNames.length > 0;
      if (!this.config.variable || !this.config.tool_name) return false;
      return this.toolParameters.every((parameter) => !parameter.required || Boolean(this.config.arguments[parameter.name]?.value));
    },
    async itemChangeHandler() {
      if (!this.item) return;
      const defaults = structuredClone(EvaluateItemsConfig['data-adapter'].payload);
      const payload = JSON.parse(JSON.stringify(this.item.payload || {}));
      this.config = {
        ...defaults,
        ...payload,
        adapter_type: payload.adapter_type || payload.data_adapter || '',
        tool_name: payload.tool_name || payload.tool || '',
        arguments: payload.arguments || {},
        tools: payload.tools || [],
      };
      this.selectedToolNames = this.config.tools.map((tool) => (typeof tool === 'string' ? tool : tool.name));
      await this.fetchTools();
      if (!this.isAgent && this.config.tool_name) {
        this.toolParameters.forEach((parameter) => this.ensureArgumentBinding(parameter));
      }
      this.$emit('valid', this.validate());
    },
  },
  watch: {
    item: {
      handler() {
        this.itemChangeHandler();
      },
      deep: true,
    },
    isAgent() {
      this.emitPayload();
    },
  },
};
</script>
